import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Apple, ArrowLeft, Check, Info, ListTodo, PackageSearch, Pencil, ShoppingBasket, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Header from "modules/main/components/header";
import { usePageTitle } from "hooks/usePageTitle";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ListItemRow } from "modules/lists/components/ListItemRow";
// import { AddItemDialog } from "modules/lists/components/AddItemDialog";
import { AddDeleteList } from "modules/lists/components/addDeleteLIst";
import { InlineProductSearch } from "modules/lists/components/InlineProductSearch";
import { CreateListItemPayload, ListItem, TemporaryQuoteItem } from "modules/lists/types/lists";
import { useLists } from "modules/lists/context/ListsContext";
import { useItemAutosave } from "modules/lists/hooks/useItemAutosave";
import MenuIcon from "modules/main/types/menus";
import { groupListItemsByCategory } from "modules/lists/utils/listCategories";
import { executeList } from "modules/lists/services/listService";
import { useCart } from "modules/cart/hooks/useCart";
import { useCartSheet } from "hooks/useCartSheet";

import { buildBulkUpdateItems, triggerBatchProductUpdates } from "modules/product/services/getUpdates";
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty"
import { Badge } from "@/components/ui/badge";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
/**
 * Page that displays and manages a single list's detail (items, name, description).
 * Route: /lists/:listUuid — view/edit mode for an existing list
 */
const ListDetailPage: React.FC = () => {
    const { setTitle,setShowBack } = usePageTitle();
    const { listUuid } = useParams<{ listUuid: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isDesktop = useMediaQuery(isDesktopSize);
    const { lists, updateList, addItem, deleteItem, loadListDetail } = useLists();

    const { cart, clearCart, applyListExecution, setResolutionMode, addDeletefromCartWithTracking } = useCart();

    // Mutable ref to avoid closure staleness in handleExecuteList setTimeout callback.
    // cart length is checked via ref so the deferred re-invocation always sees the
    // latest cart state after clearCart() has been called.
    const cartRef = useRef(cart);
    cartRef.current = cart;

    const { handleEditItem: autosaveEditItem, saveErrors } = useItemAutosave(listUuid);

    const selectedList = lists.find((l) => l.id === listUuid) ?? null;

    useEffect(() => {
        setTitle(selectedList?.name || "Lista de Compra");
        setShowBack(true);
    });

    const [isEditMode, setIsEditMode] = useState(false);
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [addItemOpen, setAddItemOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<ListItem | null>(null);
    const { open: openCartSheet, setOpen: setOpenCartSheet } = useCartSheet();
    const [isExecuting, setIsExecuting] = useState(false);
    const [showReplaceDialog, setShowReplaceDialog] = useState(false);

    // View-mode checklist state — local only, never persisted to DB
    const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(new Set());
    /** Temporary products added for quoting purposes only. Never persisted to the list. */
    const [temporaryItems, setTemporaryItems] = useState<TemporaryQuoteItem[]>([]);
    const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});
    /** Snapshot for the "Deshacer" undo when entering edit mode with checked items. */
    const checkedSnapshot = useRef<{ ids: Set<string>; qtys: Record<string, number> } | null>(null);

    // Initialise edit fields when the active list changes
    useEffect(() => {
        const startInEditMode = (location.state as { isNew?: boolean } | null)?.isNew === true;
        if (startInEditMode) {
            navigate(location.pathname, { state: {}, replace: true });
        }
        setIsEditMode(startInEditMode);
        setEditName(selectedList?.name ?? "");
        setEditDescription(selectedList?.description ?? "");
        if (listUuid) loadListDetail(listUuid);
        // Reset view-mode checklist and temporary items when navigating to a different list
        setCheckedItemIds(new Set());
        setLocalQuantities({});
        setTemporaryItems([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listUuid]);

    /** Items displayed: real items for existing list */
    const displayItems = selectedList?.items ?? [];

    // In view mode, apply local quantity overrides and split into active / excluded groups
    const effectiveItems = displayItems.map((item) => ({
        ...item,
        quantity: (!isEditMode && localQuantities[item.id] !== undefined)
            ? localQuantities[item.id]
            : item.quantity,
    }));
    const activeItems = effectiveItems.filter((item) => !checkedItemIds.has(item.id));
    const checkedItems = effectiveItems.filter((item) => checkedItemIds.has(item.id));
    const executeItemCount = activeItems.length + temporaryItems.length;
    const executeCtaLabel = isExecuting
        ? "Cotizando..."
        : `Cotizar lista · ${executeItemCount} producto${executeItemCount !== 1 ? "s" : ""}`;

    // ── Edit handlers ──────────────────────────────────────────────

    const handleSaveInlineEdit = async () => {
        if (!editName.trim() || !listUuid) return;
        await updateList(listUuid, editName.trim(), editDescription.trim());
        setLocalQuantities({});
        setIsEditMode(false);
    };

    const handleCancelInlineEdit = () => {
        setEditName(selectedList?.name ?? "");
        setEditDescription(selectedList?.description ?? "");
        setLocalQuantities({});
        setIsEditMode(false);
    };

    /**
     * Executes the list: resolves free-text items via LLM in the background and
     * adds linked items to the cart immediately. Opens the CartSheet when done.
     * Excludes items that the user has marked via checkboxes.
     */
    /**
     * Core execution logic extracted from handleExecuteList.
     * Called directly for empty cart or after user confirms clearing the cart.
     */
    const performExecution = async () => {
        try {
            setIsExecuting(true);
            // Pass excluded item IDs (checked items) to the backend
            const excludedItemIds = Array.from(checkedItemIds);
            // Pass local quantity overrides for non-excluded items
            const itemQuantities: Record<string, number> = {};
            effectiveItems
                .filter((item) => !checkedItemIds.has(item.id))
                .forEach((item) => {
                    if (localQuantities[item.id] !== undefined) {
                        itemQuantities[item.id] = localQuantities[item.id];
                    }
                });

            // Build temporary_items payload from free-text temporary items (catalogProductId === null)
            const temporaryItemsPayload = temporaryItems
                .filter((item) => item.catalogProductId === null)
                .map((item) => ({
                    tempKey: item.key,
                    rawDescription: item.rawDescription ?? item.name,
                    quantity: item.quantity,
                }));

            const execution = await executeList(
                listUuid ?? "",
                excludedItemIds,
                itemQuantities,
                temporaryItemsPayload,
            );

            setOpenCartSheet(true);

            const insertedItems = await applyListExecution(execution);
            setResolutionMode(execution);

            // Linked temporary items (user manually picked a product) —
            // add directly to cart since they already have a catalogProductId.
            const linkedTemporaryResults = await Promise.all(temporaryItems
                .filter((item) => item.catalogProductId !== null)
                .map(async (item) => {
                    const insertedSku = await addDeletefromCartWithTracking({
                        id: item.catalogProductId!,
                        sku: item.sku ?? String(item.catalogProductId),
                        name: item.name,
                        category_id: item.categoryId,
                        quantity: item.quantity,
                    });
                    return { productId: item.catalogProductId!, sku: insertedSku };
                }));
            // Keep only successfully-inserted linked temporaries so the bulk
            // trigger doesn't carry over no-op product ids.
            const linkedTemporaryProductIds = linkedTemporaryResults
                .filter((r): r is { productId: string; sku: string } => r.sku !== null)
                .map((r) => r.productId);
            const bulkItems = buildBulkUpdateItems(insertedItems, linkedTemporaryProductIds);
            if (bulkItems.length > 0) {
                void (async () => {
                    try {
                        await triggerBatchProductUpdates(bulkItems);
                    } catch {
                        // Fire-and-forget: list execution and cart opening must not depend on reception acknowledgement.
                    }
                })();
            }
        } catch {
            toast.error("No se pudo cotizar la lista. Intenta de nuevo.");
        } finally {
            setIsExecuting(false);
        }
    };

    /**
     * Executes the list: resolves free-text items via LLM in the background and
     * adds linked items to the cart immediately. Opens the CartSheet when done.
     * Excludes items that the user has marked via checkboxes.
     * Guards against cart duplication: shows a confirmation dialog when the cart
     * already has items.
     */
    const handleExecuteList = async () => {
        if (!listUuid || isExecuting) return;
        // Persist any in-progress inline edit before executing
        if (isEditMode) await handleSaveInlineEdit();
        // Guard: if cart has items, ask user to confirm replacement
        if (cartRef.current.length > 0) {
            setShowReplaceDialog(true);
            return;
        }
        await performExecution();
    };

    // ── Item handlers ──────────────────────────────────────────────

    const handleAddItem = async (data: CreateListItemPayload) => {
        if (!listUuid) return;
        await addItem(listUuid, data);
    };

    /**
     * Adds a product as a temporary quote item (view mode only).
     * - When catalogProductId is set, creates a linked temporary item (existing behavior).
     * - When catalogProductId is null, creates a free-text temporary item with rawDescription.
     * Only skips when catalogProductId is undefined (not provided at all).
     * @param data - Payload from InlineProductSearch (catalogProductId may be null for free-text)
     */
    const handleAddTemporaryItem = async (data: CreateListItemPayload): Promise<void> => {
        if (data.catalogProductId === undefined) return;
        const newItem: TemporaryQuoteItem = {
            key: crypto.randomUUID(),
            catalogProductId: data.catalogProductId,
            sku: data.catalogProductSku ?? null,
            name: data.name ?? data.rawDescription,
            categoryId: data.categoryId ?? 0,
            imageUrl: null,
            quantity: data.quantity,
            rawDescription: data.catalogProductId === null ? data.rawDescription : undefined,
        };
        setTemporaryItems((prev) => [...prev, newItem]);
    };

    /**
     * Removes a temporary quote item by its unique key.
     * @param key - The key of the item to remove
     */
    const handleRemoveTemporaryItem = (key: string) => {
        setTemporaryItems((prev) => prev.filter((item) => item.key !== key));
    };

    /**
     * Updates the quantity of a temporary quote item.
     * Removing at qty 1 deletes the item.
     * @param key - The key of the item to update
     * @param newQty - New quantity value
     */
    const handleUpdateTemporaryQuantity = (key: string, newQty: number) => {
        if (newQty <= 0) {
            setTemporaryItems((prev) => prev.filter((item) => item.key !== key));
        } else {
            setTemporaryItems((prev) =>
                prev.map((item) => item.key === key ? { ...item, quantity: newQty } : item)
            );
        }
    };

    const handleEditItem = async (data: CreateListItemPayload) => {
        if (!editingItem || !listUuid) return;
        autosaveEditItem(editingItem.id, data);
    };

    const handleDeleteItem = async (itemId: string) => {
        if (!listUuid) return;
        try {
            await deleteItem(listUuid, itemId);
        } catch {
            toast.error("No se pudo eliminar el producto. Intenta de nuevo.");
        }
    };

    // ── View-mode checklist handlers (≠ edit mode, never persist to DB) ─────

    /**
     * Updates item quantity locally. Reaching 0 moves the item to the excluded section.
     */
    const handleViewModeQuantityChange = (item: ListItem, newQty: number) => {
        if (newQty <= 0) {
            setLocalQuantities((prev) => ({ ...prev, [item.id]: 0 }));
            setCheckedItemIds((prev) => new Set(prev).add(item.id));
        } else {
            setLocalQuantities((prev) => ({ ...prev, [item.id]: newQty }));
        }
    };

    /**
     * Toggles an item’s excluded status. Unchecking a qty-0 item restores its quantity to 1.
     */
    const handleToggleCheck = (itemId: string) => {
        setCheckedItemIds((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
                // If the item reached the excluded section by qty=0, restore to 1
                setLocalQuantities((q) => {
                    if ((q[itemId] ?? 1) === 0) return { ...q, [itemId]: 1 };
                    return q;
                });
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    // ── Not found state ────────────────────────────────────────────

    if (!selectedList) {
        return (
            <>
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <ListTodo className="h-12 w-12 opacity-30" />
                    <p className="text-sm text-muted-foreground">Lista no encontrada.</p>
                    <Button variant="ghost" onClick={() => navigate("/lists")}>
                        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
                    </Button>
                </div>
            </>
        );
    }

    // ── Render ─────────────────────────────────────────────────────

    return (
        <>
            <div className="relative flex flex-col w-full h-full gap-4 py-4 px-6 lg:p-8 overflow-x-hidden">
                <div className="flex flex-col max-w-2xl w-full mx-auto gap-2">
                    <Badge variant="outline" className="">
                        {(!isEditMode) ? "Cotizando lista" : "Editando Lista"}
                    </Badge>
                    {/* Name + description + edit controls */}
                    <div className="flex w-full justify-between items-start mb-4">
                        <div className="flex flex-col gap-1 flex-1 mr-2">
                            {isEditMode ? (
                                <input
                                    className="border-input bg-transparent text-lg font-semibold outline-none focus:border-primary w-full"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Nombre de la lista"
                                    autoFocus
                                />
                            ) : (
                                <h1 className="text-lg font-semibold">{selectedList?.name ?? ""}</h1>
                            )}
                            {isEditMode ? (
                                <input
                                    className="border-input bg-transparent text-sm text-muted-foreground outline-none focus:border-primary w-full"
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    placeholder="Compra mensual del super"
                                />
                            ) : (
                                (selectedList?.description) && (
                                    <span className="text-sm text-muted-foreground">
                                        {selectedList?.description}
                                    </span>
                                )
                            )}
                            {
                                (!isEditMode)&&(
                                <div className="flex gap-2 text-muted-foreground">
                                    
                                    <Badge variant={"secondary"} className="bg-green-50">
                                        <ShoppingBasket className="h-3 w-3" />
                                        {activeItems.length} a cotizar</Badge>
                                    {checkedItems.length > 0 && (
                                        <>
                                        
                                        <Badge variant={"outline"}>{checkedItems.length} excluido{checkedItems.length !== 1 ? "s" : ""}</Badge>
                                        </>
                                    )}
                                </div>
                                )
                            }
                        </div>
                        <div className="flex gap-1 shrink-0">
                            {isEditMode ? (
                                <>
                                    <Button
                                        variant="outline"
                                        size={isDesktop ? "lg" : "xs"}
                                        className="px-2"
                                        onClick={handleSaveInlineEdit}
                                    >
                                        <Check className="h-4 w-4" />
                                        {(!isDesktop && "¡Listo!")}
                                    </Button>                                
                                </>
                            ) : (
                                <Button
                                    variant="outline"
                                    size={isDesktop ? "lg" : "xs"}
                                    className="text-muted-foreground"
                                    onClick={() => {
                                        setEditName(selectedList!.name);
                                        setEditDescription(selectedList!.description ?? "");
                                        if (checkedItemIds.size > 0) {
                                            checkedSnapshot.current = {
                                                ids: new Set(checkedItemIds),
                                                qtys: { ...localQuantities },
                                            };
                                            const count = checkedItemIds.size;
                                            setCheckedItemIds(new Set());
                                            setLocalQuantities({});
                                            toast(`${count} producto${count !== 1 ? "s" : ""} omitido${count !== 1 ? "s" : ""} se ${count !== 1 ? "reiniciaron" : "reinició"} al editar`, {
                                                position: "bottom-center",
                                                duration: 5000,
                                                action: {
                                                    label: "Deshacer",
                                                    onClick: () => {
                                                        if (checkedSnapshot.current) {
                                                            setCheckedItemIds(checkedSnapshot.current.ids);
                                                            setLocalQuantities(checkedSnapshot.current.qtys);
                                                            checkedSnapshot.current = null;
                                                        }
                                                        setIsEditMode(false);
                                                    },
                                                },
                                            });
                                        } else {
                                            setLocalQuantities({});
                                        }
                                        setIsEditMode(true);
                                    }}
                                >
                                    <Pencil className="h-4 w-4" /> 
                                    {(!isDesktop && "Editar")}
                                </Button>
                            )}
                    {displayItems.length > 0 && isDesktop && (
                    <div className="hidden lg:flex w-full justify-end max-w-2xl mx-auto" data-testid="desktop-execute-cta-container">
                        <Button
                            data-testid="desktop-execute-cta"
                            variant="default"
                            size="lg"
                            className=""
                            disabled={isExecuting}
                            onClick={handleExecuteList}
                        >
                            <PackageSearch className="h-4 w-4 mr-1" />
                            {executeCtaLabel} 
                        </Button>
                    </div>
                )}
                        </div>                        
                    </div>

                    {/* Inline product search — edit mode adds to the list, view mode adds temporary quote items */}
                    {isEditMode ? (
                        <div className="mb-4">
                            <InlineProductSearch onAdd={handleAddItem} />
                        </div>
                    ) : (
                        <div className="mb-4">
                            {/* <div className="flex items-center gap-2 mb-3">
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-xs font-medium text-muted-foreground shrink-0">
                                    Agregar solo para cotizar
                                </span>
                                <div className="flex-1 h-px bg-border" />
                            </div> */}
                            <InlineProductSearch placeholder="¿Incluimos algo más?" onAdd={handleAddTemporaryItem} />
                            {temporaryItems.length > 0 && (
                                <div className="flex flex-col gap-1.5 mt-3">
                                    {temporaryItems.map((item) => (
                                        <div
                                            key={item.key}
                                            className="flex items-center justify-between gap-3 rounded-lg border border-dashed bg-muted/30 px-3 py-2"
                                        >
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span className="text-sm font-medium truncate">{item.name}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {item.catalogProductId === null
                                                        ? "Sin preferencia — el optimizador elegirá"
                                                        : "Solo para esta compra · no se guarda en tu lista"}
                                                </span>
                                            </div>
                                            <AddDeleteList
                                                currQuantity={item.quantity}
                                                onIncrement={() => handleUpdateTemporaryQuantity(item.key, item.quantity + 1)}
                                                onDecrement={() => handleUpdateTemporaryQuantity(item.key, item.quantity - 1)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}


                    {/* Items list */}
                    <div className="flex flex-col gap-2 w-full min-w-0 max-w-full overflow-hidden">
                        {displayItems.length === 0 ? (
                            <Empty>
                            <EmptyHeader>
                                <EmptyMedia variant="icon">
                                <Apple />
                                </EmptyMedia>
                                <EmptyTitle>Tu lista está vacía</EmptyTitle>
                                <EmptyDescription>
                                Tu lista está vacía. <span className="font-semibold">Escribe lo que necesitas arriba y te ayudamos a encontrarlo.</span>
                                </EmptyDescription>
                            </EmptyHeader>
                            </Empty>
                        ) : (
                            <>
                                {!isEditMode && activeItems.length > 0 ? (
                                    // View mode: grouped by category
                                    groupListItemsByCategory(activeItems).map((group) => (
                                        <div key={group.category.id} className="w-full mt-2 first:mt-0">
                                            <div className="mb-1 flex items-center gap-2 rounded-md  px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                                <MenuIcon iconName={group.category.icon} size={14} className="" />
                                                <span>{group.category.name}</span>
                                            </div>
                                            <div className="flex flex-col gap-2 w-full">
                                                {group.items.map((item) => (
                                                    <ListItemRow
                                                        key={item.id}
                                                        item={item}
                                                        isEditMode={false}
                                                        isChecked={false}
                                                        saveError={saveErrors[item.id]}
                                                        onEdit={(it) => {
                                                            setEditingItem(it);
                                                            setAddItemOpen(true);
                                                        }}
                                                        onDelete={handleDeleteItem}
                                                        onQuantityChange={(it, newQty) =>
                                                            handleViewModeQuantityChange(it, newQty)
                                                        }
                                                        onToggleCheck={() => handleToggleCheck(item.id)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    // Edit mode: flat list
                                    activeItems.map((item) => (
                                        <ListItemRow
                                            key={item.id}
                                            item={item}
                                            isEditMode={isEditMode}
                                            isChecked={false}
                                            saveError={saveErrors[item.id]}
                                            onEdit={(it) => {
                                                setEditingItem(it);
                                                setAddItemOpen(true);
                                            }}
                                            onDelete={handleDeleteItem}
                                            onQuantityChange={(it, newQty) =>
                                                autosaveEditItem(it.id, {
                                                    rawDescription: it.rawDescription,
                                                    catalogProductId: it.catalogProductId,
                                                    quantity: newQty,
                                                    unit: it.unit,
                                                    notes: it.notes,
                                                })
                                            }
                                            onToggleCheck={() => handleToggleCheck(item.id)}
                                            onRawDescriptionChange={(itemId, newRawDescription) =>
                                                autosaveEditItem(itemId, {
                                                    rawDescription: newRawDescription,
                                                    catalogProductId: item.catalogProductId,
                                                    quantity: item.quantity,
                                                    unit: item.unit,
                                                    notes: item.notes,
                                                })
                                            }
                                        />
                                    ))
                                )}
                                {checkedItems.length > 0 && (
                                    <>
                                        <div className="flex items-center gap-2 mt-3 mb-1">
                                            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                                                No incluir en la compra
                                            </span>
                                            <div className="flex-1 h-px bg-border" />
                                        </div>
                                        {checkedItems.map((item) => (
                                            <ListItemRow
                                                key={item.id}
                                                item={item}
                                                isEditMode={isEditMode}
                                                isChecked={true}
                                                saveError={saveErrors[item.id]}
                                                onEdit={(it) => {
                                                    setEditingItem(it);
                                                    setAddItemOpen(true);
                                                }}
                                                onDelete={handleDeleteItem}
                                                onToggleCheck={() => handleToggleCheck(item.id)}
                                                onRawDescriptionChange={(itemId, newRawDescription) =>
                                                    autosaveEditItem(itemId, {
                                                        rawDescription: newRawDescription,
                                                        catalogProductId: item.catalogProductId,
                                                        quantity: item.quantity,
                                                        unit: item.unit,
                                                        notes: item.notes,
                                                    })
                                                }
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="w-full fixed bottom-0 left-0 p-4 bg-background border-t lg:hidden" data-testid="mobile-execute-cta-container">
                {/* Cotizar button */}
                {displayItems.length > 0 && (
                    <Button
                        variant="default"
                        size="lg"
                        className="w-full my-2"
                        disabled={isExecuting}
                        onClick={handleExecuteList}
                    >
                        <PackageSearch className="h-4 w-4 mr-1" />
                        {executeCtaLabel}
                    </Button>
                )}
                </div>
            </div>

            {/* <AddItemDialog
                open={addItemOpen}
                onOpenChange={setAddItemOpen}
                initialData={
                    editingItem
                        ? {
                            id: editingItem.id,
                            rawDescription: editingItem.rawDescription,
                            catalogProductId: editingItem.catalogProductId,
                            quantity: editingItem.quantity,
                            unit: editingItem.unit,
                            notes: editingItem.notes,
                        }
                        : undefined
                }
                onAdd={editingItem ? handleEditItem : handleAddItem}
            /> */}

            <AlertDialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Reemplazar carrito actual?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tu carrito tiene {cart.length} producto{cart.length !== 1 ? "s" : ""}.
                            Al cotizar esta lista se eliminarán los productos actuales.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { clearCart(); setShowReplaceDialog(false); setTimeout(() => handleExecuteList(), 0); }}>
                            Reemplazar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export { ListDetailPage };
